export const DEFAULT_AUTH_NEXT_PATH = "/portal/dashboard";

const AUTH_PATH_SENTINEL_ORIGIN = "https://auth-next.invalid";
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f-\u009f]/;
const ESCAPE_LIKE_PERCENT = /%[0-9a-f]/i;
const MAX_DECODE_PASSES = 4;

function parseSameOriginPath(value: string) {
  try {
    const parsed = new URL(value, AUTH_PATH_SENTINEL_ORIGIN);
    if (
      parsed.origin !== AUTH_PATH_SENTINEL_ORIGIN ||
      parsed.username ||
      parsed.password ||
      parsed.pathname.startsWith("//")
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function isSafeAuthNextPath(value: string) {
  let probe = value;

  for (let pass = 0; pass <= MAX_DECODE_PASSES; pass += 1) {
    if (
      !probe.startsWith("/") ||
      probe.startsWith("//") ||
      probe.includes("\\") ||
      CONTROL_CHARACTERS.test(probe) ||
      probe.trim() !== probe ||
      !parseSameOriginPath(probe)
    ) {
      return false;
    }

    let decoded: string;
    try {
      decoded = decodeURIComponent(probe);
    } catch {
      // The submitted value itself must be valid percent encoding. On later
      // passes, a valid %25 may have produced a literal percent (for example,
      // `100%25`). Only an unambiguous literal percent is accepted; anything
      // followed by a hexadecimal escape nibble fails closed as malformed
      // multi-stage encoding.
      if (pass === 0 || ESCAPE_LIKE_PERCENT.test(probe)) return false;
      return true;
    }

    if (decoded === probe) return true;
    if (pass === MAX_DECODE_PASSES) return false;
    probe = decoded;
  }

  return false;
}

export function resolveAuthNextPath(nextParam: unknown) {
  if (typeof nextParam !== "string" || !nextParam || !isSafeAuthNextPath(nextParam)) {
    return DEFAULT_AUTH_NEXT_PATH;
  }

  const parsed = parseSameOriginPath(nextParam);
  if (!parsed) return DEFAULT_AUTH_NEXT_PATH;

  const pathname = parsed.pathname.replace(/^\/(?:SA_admin|sa_admin)(?=\/|$)/, "/_sa_admin");
  const resolvedPath = `${pathname}${parsed.search}${parsed.hash}`;
  return isSafeAuthNextPath(resolvedPath) ? resolvedPath : DEFAULT_AUTH_NEXT_PATH;
}
